/**
 * Public accept-invite page. The recipient clicks a link in their
 * invite email (carrying the plaintext token in the query string)
 * and lands here. Possible states:
 *
 *   - Token missing or unknown → "Invite not found"
 *   - Token expired / revoked / consumed → matching message
 *   - Token valid, user signed out → "Sign in or sign up as X to accept"
 *   - Token valid, signed in with wrong email → "Sign out and try again"
 *   - Token valid, signed in with right email → "Join {tenant}" button
 *
 * After a successful accept, redirect to /admin/<slug> so the new
 * member lands inside their workspace.
 */
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, AlertDescription, AlertTitle, Button, Card } from '@bookaburra/ui';
import { CheckCircle2, LogIn, MailWarning, ShieldAlert } from 'lucide-react';
import { authClient } from '../auth/client';
import { AppHeader } from '../components/AppHeader';
import { useDocumentTitle } from '../lib/use-document-title';
import { formatRole } from '../lib/format-role';

interface AcceptInviteSearch {
  token?: string;
}

export const Route = createFileRoute('/accept-invite')({
  validateSearch: (search: Record<string, unknown>): AcceptInviteSearch => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: AcceptInvitePage,
});

interface InvitePreview {
  status: 'valid' | 'expired' | 'revoked' | 'consumed' | 'not_found';
  tenantName?: string;
  tenantSlug?: string;
  role?: 'owner' | 'manager' | 'staff';
  email?: string;
  expiresAt?: number;
}

async function previewInvite(token: string): Promise<InvitePreview> {
  const r = await fetch('/api/invites/preview', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!r.ok) return { status: 'not_found' };
  return r.json();
}

async function acceptInvite(token: string): Promise<{ tenant: { slug: string; name: string } }> {
  const r = await fetch('/api/invites/accept', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  const body = (await r.json().catch(() => ({}))) as {
    error?: string;
    tenant?: { slug: string; name: string };
  };
  if (!r.ok || !body.tenant) {
    throw new Error(body.error || `accept_failed_${r.status}`);
  }
  return { tenant: body.tenant };
}

function AcceptInvitePage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  useDocumentTitle('Accept invite');

  const { data: session } = authClient.useSession();
  const previewQ = useQuery({
    queryKey: ['invite-preview', token ?? ''],
    queryFn: () => previewInvite(token!),
    enabled: Boolean(token),
    staleTime: 30_000,
  });

  const [accepting, setAccepting] = useState(false);
  const acceptMut = useMutation({
    mutationFn: () => acceptInvite(token!),
    onSuccess: ({ tenant }) => {
      void navigate({ to: '/admin/$slug', params: { slug: tenant.slug } });
    },
  });

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader />
      <main className="mx-auto max-w-lg px-6 py-12 sm:px-12 sm:py-16">
        <h1 className="text-3xl font-bold tracking-tight">Accept invite</h1>

        {!token ? (
          <Alert variant="destructive" className="mt-6">
            <MailWarning className="h-4 w-4" />
            <AlertTitle>Missing token</AlertTitle>
            <AlertDescription>
              This page needs the link from your invite email. Open the email
              and tap &ldquo;Accept invite&rdquo; again.
            </AlertDescription>
          </Alert>
        ) : null}

        {token && previewQ.isLoading ? (
          <p className="mt-6 text-sm text-muted-foreground">Checking your invite&hellip;</p>
        ) : null}

        {token && previewQ.data ? (
          <PreviewBody
            preview={previewQ.data}
            signedInEmail={session?.user.email ?? null}
            accepting={accepting || acceptMut.isPending}
            errorCode={
              acceptMut.error instanceof Error
                ? (acceptMut.error.message as string)
                : null
            }
            onAccept={() => {
              setAccepting(true);
              acceptMut.mutate(undefined, { onSettled: () => setAccepting(false) });
            }}
            onSignOut={async () => {
              await authClient.signOut();
              void previewQ.refetch();
            }}
            tokenForSignin={token}
          />
        ) : null}
      </main>
    </div>
  );
}

function PreviewBody({
  preview,
  signedInEmail,
  accepting,
  errorCode,
  onAccept,
  onSignOut,
  tokenForSignin,
}: {
  preview: InvitePreview;
  signedInEmail: string | null;
  accepting: boolean;
  errorCode: string | null;
  onAccept: () => void;
  onSignOut: () => void;
  tokenForSignin: string;
}) {
  if (preview.status === 'not_found') {
    return (
      <Alert variant="destructive" className="mt-6">
        <MailWarning className="h-4 w-4" />
        <AlertTitle>Invite not found</AlertTitle>
        <AlertDescription>
          The link doesn&rsquo;t match any invite. Ask the admin who invited
          you to send a new one.
        </AlertDescription>
      </Alert>
    );
  }
  if (preview.status === 'expired') {
    return (
      <Alert variant="warning" className="mt-6">
        <MailWarning className="h-4 w-4" />
        <AlertTitle>Invite expired</AlertTitle>
        <AlertDescription>
          Invites last 7 days. Ask {preview.tenantName ?? 'the admin'} to send
          a fresh one.
        </AlertDescription>
      </Alert>
    );
  }
  if (preview.status === 'revoked') {
    return (
      <Alert variant="warning" className="mt-6">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Invite revoked</AlertTitle>
        <AlertDescription>
          The team at {preview.tenantName ?? 'this workspace'} cancelled this
          invite. Ask them to send a new one if that wasn&rsquo;t intentional.
        </AlertDescription>
      </Alert>
    );
  }
  if (preview.status === 'consumed') {
    return (
      <Alert variant="info" className="mt-6">
        <CheckCircle2 className="h-4 w-4" />
        <AlertTitle>Already accepted</AlertTitle>
        <AlertDescription>
          This invite has been used already. If that was you, you can sign in
          to <strong>{preview.tenantName}</strong> directly.
        </AlertDescription>
      </Alert>
    );
  }

  // Status: valid.
  const inviteEmail = preview.email ?? '';
  const signedInMatches =
    signedInEmail != null &&
    signedInEmail.trim().toLowerCase() === inviteEmail;

  if (signedInEmail && !signedInMatches) {
    return (
      <Card className="mt-6 p-6">
        <h2 className="text-lg font-semibold">Wrong account</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This invite is for{' '}
          <strong className="text-foreground">{inviteEmail}</strong>, but
          you&rsquo;re signed in as{' '}
          <strong className="text-foreground">{signedInEmail}</strong>. Sign
          out and back in with the invited email to continue.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={onSignOut}>Sign out</Button>
          <Link to="/">
            <Button variant="ghost">Cancel</Button>
          </Link>
        </div>
      </Card>
    );
  }

  if (!signedInEmail) {
    const signinRedirect = encodeURIComponent(
      `/accept-invite?token=${encodeURIComponent(tokenForSignin)}`,
    );
    return (
      <Card className="mt-6 p-6">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">
          You&rsquo;re invited
        </p>
        <h2 className="mt-2 text-2xl font-bold tracking-tight">
          Join {preview.tenantName} on Bookaburra
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">
          Set up your account or sign in with{' '}
          <strong className="text-foreground">{inviteEmail}</strong> to accept
          the invite as <strong>{formatRole(preview.role)}</strong>.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <Link to="/signup" search={{ email: inviteEmail, redirect: `/accept-invite?token=${tokenForSignin}` }}>
            <Button leadingIcon={<LogIn className="h-4 w-4" />}>
              Create account
            </Button>
          </Link>
          <a href={`/signin?redirect=${signinRedirect}`}>
            <Button variant="outline">I already have an account</Button>
          </a>
        </div>
      </Card>
    );
  }

  return (
    <Card className="mt-6 p-6">
      <p className="text-xs font-semibold uppercase tracking-widest text-primary">
        Ready to join
      </p>
      <h2 className="mt-2 text-2xl font-bold tracking-tight">
        Join {preview.tenantName}
      </h2>
      <p className="mt-3 text-sm text-muted-foreground">
        Signed in as <strong className="text-foreground">{inviteEmail}</strong>.
        You&rsquo;ll join as <strong>{formatRole(preview.role)}</strong>.
      </p>
      <div className="mt-5">
        <Button onClick={onAccept} loading={accepting}>
          Join {preview.tenantName}
        </Button>
      </div>
      {errorCode ? (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Couldn&rsquo;t accept</AlertTitle>
          <AlertDescription>{friendlyAcceptError(errorCode)}</AlertDescription>
        </Alert>
      ) : null}
    </Card>
  );
}

function friendlyAcceptError(code: string): string {
  switch (code) {
    case 'not_authenticated':
      return 'You need to be signed in to accept. Sign in and try again.';
    case 'email_mismatch':
      return 'Your signed-in email doesn\u2019t match the invite. Sign out and sign in with the invited address.';
    case 'expired':
      return 'This invite expired. Ask the admin to send a fresh one.';
    case 'revoked':
      return 'This invite was revoked.';
    case 'consumed':
      return 'This invite has already been accepted.';
    case 'tenant_gone':
      return 'The workspace this invite was for no longer exists.';
    case 'not_found':
      return 'Invite not found.';
    default:
      return code;
  }
}
